import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon } from '@/components/ui/icons';
import { useTranslation } from '@/i18n';
import { cx } from '@/lib/cx';
import styles from './SchedulePage.module.css';

/**
 * A column filter that searches a dictionary and commits a code.
 *
 * The interaction, with no opinion about where the options come from: the airline
 * column filters a list held in the browser, the two airport columns ask the server as
 * you type, and both behave identically to a reader. That is the whole reason this is
 * one component — the fiddly parts below are the same either way, and three copies of
 * them would be three chances to get the keyboard wrong.
 *
 * Unlike the plain text filters, typing here searches the DICTIONARY, not the table.
 * The table changes when something is chosen, and the value committed is always a real
 * IATA code. The other filters commit what you type because `ALA` is both what you
 * mean and what the endpoint matches; here `ALASKA` is what you mean and `AS` is what
 * the endpoint matches, so committing the text would ask the server for a code that
 * cannot exist and answer with an empty board.
 *
 * Leaving without choosing therefore restores the code in force: a half-typed name is
 * a search in progress, not a filter, and it must not sit in the box pretending
 * otherwise.
 *
 * The input stays UNCONTROLLED for the same reason the other filters are: a commit
 * rewrites the URL, and a controlled value keyed off the URL would reset the field
 * mid-word. The typed text is therefore read from the DOM node, and the query — the
 * only piece of it in React state — exists to drive the options, set from event
 * handlers and never from an effect.
 *
 * **`multiple`** makes it a multi-select, for the columns whose endpoint takes a list.
 * An option then TOGGLES — ticked in the list, applied at once — and the list stays
 * open for the next. The box always reads the codes in force, `ALA, NQZ`, and that
 * summary is not a search: arriving in the field or ticking an option leaves the list
 * unfiltered, and the first key typed replaces the summary with a search.
 */

export interface CodeOption {
  /** What gets committed. Options without one are the caller's to drop. */
  code: string;
  /** The human half: an airline's name, an airport's city. */
  label: string;
  /** Distinguishes two options that share a code, which dictionaries do. */
  key?: string | number;
}

interface CodeFilterProps {
  /** The codes in force — what is in the URL. At most one unless `multiple`. */
  values: readonly string[];
  onChange: (values: string[]) => void;
  /** Several codes, each option toggling in or out. See above. */
  multiple?: boolean;
  placeholder: string;
  ariaLabel: string;
  /** The options for `query`, already ranked and capped by the caller. */
  options: readonly CodeOption[];
  /**
   * Reported on every keystroke, so a caller can search a server for it.
   *
   * The caller owns the query as far as options are concerned; this component keeps
   * its own copy only to decide when the list is worth showing.
   */
  onQueryChange?: (query: string) => void;
  /** True while the caller is fetching options — the list says so instead of lying. */
  isLoading?: boolean;
  /** Shown in place of options when there are none and nothing is loading. */
  emptyLabel: string;
  /** A quiet line under the list: what was cut off, or that a search is still running. */
  footnote?: string | null;
}

export const CodeFilter = ({
  values,
  onChange,
  multiple = false,
  placeholder,
  ariaLabel,
  options,
  onQueryChange,
  isLoading = false,
  emptyLabel,
  footnote = null,
}: CodeFilterProps) => {
  const { t } = useTranslation();

  /** What the box reads away from it: the code, or the codes. */
  const value = values.join(', ');

  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  /**
   * Set while `pick` hands focus back to the input.
   *
   * Focus opens the list, and a pick made with the mouse ends by returning focus to
   * the field — which would re-open the list the pick just closed. `focus()` runs the
   * handler synchronously, so a flag around the call is enough; a timer would be a
   * race dressed up as a fix.
   */
  const returningFocus = useRef(false);
  /**
   * Set while the box holds a committed code rather than something being typed.
   *
   * The first printable key then starts a new search instead of editing that code —
   * emptying the field a keystroke before the character lands in it. The obvious
   * alternative, selecting the text so typing overwrites it, puts a highlight in the
   * box that the reader did not ask for and cannot explain: it looks like a bug the
   * moment something is chosen.
   */
  const holdsCommitted = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  /** Where to paint the menu, in viewport coordinates. See `place` below. */
  const [box, setBox] = useState<{ top: number; left: number; width: number } | null>(null);

  // Realigned from the URL only while the reader is elsewhere — a Back, a shared link
  // or "Clear all" may move it, a keystroke may not be overwritten.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    if (document.activeElement === input) return;
    if (input.value !== value) input.value = value;
  }, [value]);

  /**
   * The menu is portalled to the body and positioned in viewport coordinates.
   *
   * It has to be: the table scrolls sideways, and a container with `overflow-x: auto`
   * clips on BOTH axes — CSS computes the other axis to `auto` too — so a menu
   * rendered inside the header cell would be cut off by the table it belongs to.
   */
  const place = () => {
    const rect = inputRef.current?.getBoundingClientRect();
    if (rect) setBox({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };

  useEffect(() => {
    if (!isOpen) return;

    // Kept in step rather than closed on scroll: the list itself scrolls, and closing
    // on any scroll would shut the menu the moment someone used its scrollbar.
    const reposition = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (rect) setBox({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (inputRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    window.addEventListener('scroll', reposition, { capture: true, passive: true });
    window.addEventListener('resize', reposition);
    document.addEventListener('pointerdown', onPointerDown);

    return () => {
      window.removeEventListener('scroll', reposition, { capture: true });
      window.removeEventListener('resize', reposition);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isOpen]);

  const search = (next: string) => {
    onQueryChange?.(next);
  };

  const pick = (option: CodeOption) => {
    const code = option.code.toUpperCase();
    const input = inputRef.current;

    if (multiple) {
      const next = values.includes(code) ? values.filter((each) => each !== code) : [...values, code];
      onChange(next);

      // Open still, and the box reads the codes now in force at once — not only once
      // the field is left. They are held as committed, so the next key typed replaces
      // them with a search: the footing the field is on when first arrived in. The
      // focus goes back without the focus handler treating it as a fresh arrival.
      const summary = next.join(', ');
      if (input) input.value = summary;
      search('');
      setHighlight(-1);
      holdsCommitted.current = true;
      returningFocus.current = true;
      input?.focus();
      input?.setSelectionRange(summary.length, summary.length);
      returningFocus.current = false;
      return;
    }

    if (input) input.value = code;

    setIsOpen(false);
    setHighlight(-1);
    search(code);
    onChange([code]);

    // Focus goes back to the field, caret at the end and nothing highlighted.
    holdsCommitted.current = true;
    returningFocus.current = true;
    input?.focus();
    input?.setSelectionRange(code.length, code.length);
    returningFocus.current = false;
  };

  /** Closes the list and puts the committed code back in the box. */
  const abandon = () => {
    const input = inputRef.current;
    if (input && input.value !== value) input.value = value;

    // Marked rather than selected, for the same reason as after a pick.
    holdsCommitted.current = true;

    // A list of codes is a summary, not something to search for.
    search(multiple ? '' : value);
    setIsOpen(false);
    setHighlight(-1);
  };

  const clear = () => {
    const input = inputRef.current;
    if (input) {
      input.value = '';
      input.focus();
    }
    search('');
    setIsOpen(false);
    setHighlight(-1);
    onChange([]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // A single character with no modifier — Backspace, Tab and the arrows are edits or
    // navigation and leave the code alone.
    const printable = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

    if (holdsCommitted.current) {
      // Emptied here, so the character the browser is about to insert lands in a
      // clear field. No preventDefault, no selection, nothing for the reader to see.
      // A summary of several codes goes on Backspace and Delete too: editing
      // `ALA, NQZ` a character at a time would be searching for `ALA, NQ`.
      const erasing = multiple && (event.key === 'Backspace' || event.key === 'Delete');
      if (printable || erasing) event.currentTarget.value = '';
      holdsCommitted.current = false;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      // Opening with the keyboard is how this is reachable without a mouse.
      event.preventDefault();
      if (!isOpen) {
        place();
        setIsOpen(true);
        setHighlight(0);
        return;
      }

      const step = event.key === 'ArrowDown' ? 1 : -1;
      const count = options.length;
      if (count === 0) return;
      setHighlight((current) => (current + step + count) % count);
      return;
    }

    if (event.key === 'Enter' && isOpen) {
      // The highlighted one, or the only one: having typed a name down to a single
      // match, pressing Enter can mean nothing else.
      const chosen = options[highlight] ?? (options.length === 1 ? options[0] : undefined);
      if (chosen) {
        event.preventDefault();
        pick(chosen);
      }
      return;
    }

    if (event.key === 'Escape' && isOpen) {
      // Abandons the search and restores the filter in force, which is what Escape
      // means everywhere else in a browser.
      event.preventDefault();
      abandon();
    }
  };

  return (
    <span className={styles.filterCell}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        className={cx(styles.filterInput, value !== '' && styles.filterActive)}
        defaultValue={value}
        placeholder={placeholder}
        // The whole list, for when the column is too narrow to show it.
        title={values.length > 1 ? value : undefined}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={isOpen && highlight >= 0 ? `${listId}-${highlight}` : undefined}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => {
          const input = event.currentTarget;

          // Uppercased in place with the caret restored: a code is upper case, and
          // without this, correcting the middle of one sends the cursor to the end.
          const caret = input.selectionStart;
          input.value = input.value.toUpperCase();
          if (caret !== null) input.setSelectionRange(caret, caret);

          // Searches, and nothing more: no request for the TABLE goes out until
          // something is chosen. The first option is highlighted so Enter has
          // something to take, but an empty field highlights nothing — Enter on a full
          // list would otherwise pick whichever row happens to sort first.
          const next = input.value;
          search(next);
          setHighlight(next === '' ? -1 : 0);
          place();
          setIsOpen(true);
        }}
        onFocus={(event) => {
          if (returningFocus.current) return;

          // Clicking into the field opens the list: this is a dropdown that can also
          // be typed in, so arriving in it should show what there is to choose from.
          // The query is re-read from the DOM because a clear, a Back or a shared link
          // may have changed the field since the last keystroke. A multi-select's
          // box holds a summary instead, so its list opens unfiltered and the first
          // key replaces the summary.
          if (multiple) {
            holdsCommitted.current = true;
            search('');
          } else {
            search(event.currentTarget.value);
          }
          setHighlight(-1);
          place();
          setIsOpen(true);
        }}
        onMouseDown={(event) => {
          const input = event.currentTarget;

          // A first click selects what is there, so typing REPLACES the code in force
          // rather than appending to it: this box holds `TK`, and someone clicking it
          // to look for Emirates means Emirates, not `TKEMIR`.
          //
          // It takes a mousedown handler because focus alone cannot do it — the caret
          // is placed on mouseup, which collapses any selection made on focus. A
          // second click, once the field already has focus, is left alone so the text
          // can still be edited by hand.
          if (document.activeElement === input) return;

          event.preventDefault();
          input.focus();
          input.select();
          // Selected on purpose here: the reader just clicked the box, so a highlight
          // is the answer to what they did rather than an unexplained state.
          holdsCommitted.current = false;
        }}
        onClick={() => {
          // A second click, after a pick closed the menu, opens it again rather than
          // leaving a control that looks like a dropdown and does nothing.
          place();
          setIsOpen(true);
        }}
        onBlur={(event) => {
          // A click on an option lands after blur, so the menu must survive focus
          // moving into it.
          if (menuRef.current?.contains(event.relatedTarget as Node | null)) return;
          abandon();
        }}
        onKeyDown={handleKeyDown}
      />

      <button
        type="button"
        className={styles.filterClear}
        onClick={clear}
        aria-label={t('schedule.clearFilter', { name: ariaLabel })}
      >
        ×
      </button>

      {isOpen &&
        box !== null &&
        createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            aria-multiselectable={multiple || undefined}
            className={styles.comboMenu}
            style={{ top: box.top, left: box.left, minWidth: Math.max(box.width, 260) }}
          >
            {options.length === 0 ? (
              <p className={styles.comboNote}>{isLoading ? t('common.loading') : emptyLabel}</p>
            ) : (
              options.map((option, index) => {
                // In a multi-select, `aria-selected` is the tick and the highlight is
                // the active descendant; in a single one the highlight is the choice.
                const ticked = multiple && values.includes(option.code.toUpperCase());

                return (
                  <button
                    key={option.key ?? option.code}
                    id={`${listId}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={multiple ? ticked : index === highlight}
                    className={cx(styles.comboOption, index === highlight && styles.comboActive)}
                    // The mouse moving over an option is the reader pointing at it, so
                    // the keyboard highlight follows rather than fighting it.
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => pick(option)}
                  >
                    {multiple && (
                      <span className={cx(styles.comboCheck, ticked && styles.comboChecked)} aria-hidden="true">
                        {ticked && <CheckIcon />}
                      </span>
                    )}
                    <span className={styles.comboCode}>{option.code}</span>
                    <span className={styles.comboLabel}>{option.label}</span>
                  </button>
                );
              })
            )}

            {footnote !== null && options.length > 0 && (
              <p className={styles.comboNote}>{footnote}</p>
            )}
          </div>,
          document.body,
        )}
    </span>
  );
};
